/**
 * GenericCell Component
 *
 * Generic wrapper that looks up and renders the appropriate cell component
 * from the registry based on entity type and field name.
 *
 * This is the primary integration point for QueryBuilder to use entity
 * table cell components.
 */

import React from 'react';

import { formatFieldValue } from '@/sections/insights/queryBuilder/legacy/utils/queryResultHelpers';

import { DefaultCell } from './DefaultCell';
import { EntityType, getCellComponent } from './registry';

export interface GenericCellProps {
  entity: any;
  field: string;
  entityType: EntityType;
  column?: {
    entityType: string;
    arrayMaxVisible?: number;
    wrapInTooltip?: boolean;
  };
}

/**
 * Maps entity types to their expected prop names in cell components
 *
 * Most entity-specific cells use entity-specific prop names (e.g., 'asset', 'seed')
 * rather than a generic 'entity' prop.
 */
const ENTITY_PROP_NAMES: Record<EntityType, string> = {
  Asset: 'asset',
  Seed: 'seed',
  Technology: 'technology',
  Vulnerability: 'vulnerability',
  ActiveDirectory: 'entity',
};

/**
 * Generic cell renderer using registry lookup
 *
 * Lookup strategy:
 * 1. Try to get cell component from registry
 * 2. If found, render with component using entity-specific prop name
 * 3. If not found, format value and render with DefaultCell
 *
 * @example
 * ```typescript
 * <GenericCell
 *   entity={asset}
 *   field="status"
 *   entityType="Asset"
 * />
 * // Renders: <AssetStatusComponent asset={asset} />
 * ```
 */
export const GenericCell: React.FC<GenericCellProps> = React.memo(
  ({ entity, field, entityType, column }) => {
    // Try to get cell component from registry
    const CellComponent = getCellComponent(entityType, field);

    if (CellComponent) {
      // Get the entity-specific prop name (e.g., 'asset', 'seed')
      const propName = ENTITY_PROP_NAMES[entityType];

      // Pass through maxVisible and wrapInTooltip for array-based cells
      const props = {
        [propName]: entity,
        ...(column?.arrayMaxVisible !== undefined && {
          maxVisible: column.arrayMaxVisible,
        }),
        ...(column?.wrapInTooltip !== undefined && {
          wrapInTooltip: column.wrapInTooltip,
        }),
      };

      // Use React.createElement for dynamic components to avoid React 19 linter error
      return React.createElement(CellComponent, props);
    }

    // Fallback: format value and render with DefaultCell
    const formattedValue = formatFieldValue(
      entity,
      field,
      column || { entityType }
    );

    return (
      <DefaultCell
        value={formattedValue}
        maxVisible={column?.arrayMaxVisible}
      />
    );
  }
);

GenericCell.displayName = 'GenericCell';
